import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthLayout from '../components/layout/AuthLayout';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import {
  roleLabels, roleColors, roleIcons, modules,
} from '../data/mockData';
import './AuthPage.css';

function validEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function initials(prenom, nom) {
  return ((prenom[0] || '') + (nom[0] || '')).toUpperCase();
}

function AuthPage() {
  const navigate = useNavigate();
  const { login, user } = useAuth();

  const [formMode, setFormMode] = useState('connexion');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [currentRole, setCurrentRole] = useState('');
  const [filiales, setFiliales] = useState([]);
  const [departements, setDepartements] = useState([]);
  const [regValues, setRegValues] = useState({
    prenom: '', nom: '', email: '', tel: '',
    filiale_id: '', departement_id: '', poste: '', pwd: '', confirm: '',
  });
  const [regErrors, setRegErrors] = useState({});
  const [showPwd, setShowPwd] = useState({ login: false, reg: false, confirm: false });
  const [successMessage, setSuccessMessage] = useState(false);

  // Rediriger si déjà connecté
  useEffect(() => {
    if (user) navigateToDashboard(user);
  }, [user]); // eslint-disable-line

  // Charger filiales et départements
  useEffect(() => {
    if (formMode === 'inscription' && currentStep === 2) {
      // TODO: Implémenter les endpoints filiales et departements dans le backend
      // api.filiales().then(d => setFiliales(d.filiales || [])).catch(() => {});
      // api.departements().then(d => setDepartements(d.departements || [])).catch(() => {});
    }
  }, [formMode, currentStep]);

  const particles = useMemo(() => Array.from({ length: 20 }, () => ({
    left: `${Math.random() * 100}%`,
    width: `${Math.random() * 3 + 1}px`,
    height: `${Math.random() * 3 + 1}px`,
    opacity: Math.random() * 0.25 + 0.05,
    duration: `${Math.random() * 10 + 12}s`,
    delay: `${Math.random() * 8}s`,
  })), []);

  const navigateToDashboard = (u) => {
    console.log('🔍 Navigation - User:', u);
    console.log('🔍 Département:', u.departement);
    
    if (u.role === 'dg') {
      console.log('✅ Redirection vers dashboard-dg');
      return navigate('/dashboard-dg');
    }
    
    if (u.role === 'chef') {
      const dept = (u.departement?.nom || u.departement || '').toLowerCase();
      console.log('🔍 Département (lowercase):', dept);
      
      // IMPORTANT: Tester "caisse" AVANT "rh" pour éviter les conflits
      if (dept.includes('caisse') || dept.includes('secrétariat')) {
        console.log('✅ Redirection vers dashboard-caisse');
        return navigate('/dashboard-caisse');
      }
      if (dept.includes('comptab') || dept.includes('financ')) {
        console.log('✅ Redirection vers dashboard-comptabilite');
        return navigate('/dashboard-comptabilite');
      }
      if (dept.includes('immob') || dept.includes('location')) {
        console.log('✅ Redirection vers dashboard-location');
        return navigate('/dashboard-location');
      }
      if (dept.includes('rh') || dept.includes('administration')) {
        console.log('✅ Redirection vers dashboard-rh');
        return navigate('/dashboard-rh');
      }
      if (dept.includes('projet')) {
        console.log('✅ Redirection vers dashboard-projets');
        return navigate('/dashboard-projets');
      }
      if (dept.includes('techno')) {
        console.log('✅ Redirection vers dashboard-technologie');
        return navigate('/dashboard-technologie');
      }
      if (dept.includes('marketing')) {
        console.log('✅ Redirection vers dashboard-marketing');
        return navigate('/dashboard-marketing');
      }
      console.log('⚠️ Aucune correspondance, redirection vers tdb-employes');
      return navigate('/tdb-employes');
    }
    
    if (u.role === 'employee' || u.role === 'employe') {
      console.log('✅ Redirection vers tdb-employe');
      return navigate('/tdb-employe');
    }

    console.log('✅ Redirection vers tdb-individuel');
    navigate('/tdb-individuel');
  };

  const loginUser = async () => {
    if (!loginEmail || !loginPassword) {
      setLoginError('⚠️ Veuillez remplir tous les champs.');
      return;
    }
    setLoginLoading(true);
    setLoginError('');
    try {
      const data = await login(loginEmail, loginPassword);
      navigateToDashboard(data.user);
    } catch (err) {
      setLoginError(err?.message || '⚠️ Email ou mot de passe incorrect.');
    } finally {
      setLoginLoading(false);
    }
  };

  const nextStep = (from) => {
    setRegErrors({});
    if (from === 1) {
      const errors = {};
      if (!regValues.prenom.trim()) errors.prenom = '⚠️ Prénom requis';
      if (!regValues.nom.trim()) errors.nom = '⚠️ Nom requis';
      if (!validEmail(regValues.email)) errors.email = '⚠️ Email invalide';
      if (!regValues.tel.trim()) errors.tel = '⚠️ Téléphone requis';
      setRegErrors(errors);
      if (!Object.keys(errors).length) setCurrentStep(2);
      return;
    }
    if (from === 2) {
      const errors = {};
      if (!currentRole) errors.role = '⚠️ Sélectionnez un rôle';
      if (!regValues.filiale_id) errors.filiale_id = '⚠️ Filiale requise';
      if (!regValues.departement_id) errors.departement_id = '⚠️ Département requis';
      if (!regValues.poste.trim()) errors.poste = '⚠️ Poste requis';
      setRegErrors(errors);
      if (!Object.keys(errors).length) setCurrentStep(3);
    }
  };

  const handleRegister = async () => {
    const errors = {};
    if (regValues.pwd.length < 6) errors.pwd = '⚠️ Minimum 6 caractères';
    if (regValues.pwd !== regValues.confirm) errors.confirm = '⚠️ Les mots de passe ne correspondent pas';
    setRegErrors(errors);
    if (Object.keys(errors).length) return;

    try {
      // TODO: Implémenter l'endpoint register dans le backend
      // const data = await api.auth.register({...});
      setRegErrors({ general: "L'inscription n'est pas encore implémentée" });
    } catch (err) {
      const msgs = err?.errors ? Object.values(err.errors).flat().join(' ') : err?.message || 'Erreur lors de l\'inscription';
      setRegErrors({ general: msgs });
    }
  };

  const selectedRoleClass = (role) => (currentRole === role ? 'role-card selected' : 'role-card');

  return (
    <AuthLayout>
      <div className="auth-app">
        <div id="particles">
          {particles.map((particle, index) => (
            <div key={index} className="particle" style={{
              left: particle.left, width: particle.width, height: particle.height,
              opacity: particle.opacity, animationDuration: particle.duration, animationDelay: particle.delay,
            }} />
          ))}
        </div>

        <div className="left-panel">
          <div className="logo-wrap">
            <div className="logo-icon">⚡</div>
            <div className="logo-name">FHC Groupe SARL</div>
            <div className="logo-sub">Plateforme de Gestion Digitale</div>
          </div>
          <div className="filiaires-label">Nos entités</div>
          <div className="filiaires">
            {[['🏛️','FHC Conseil','Conseil & accompagnement PME'],['🏠','FHC Location','Immobilier digital (AfricaLocation)'],['💻','FHC Technologie','Création de sites web & digital'],['🚀','FHC Entrepreneuriat','Accompagnement entrepreneurs'],['📚','FHC Formation','Formation professionnelle'],['💼','EMPLOI+','Insertion professionnelle — StagesBénin']].map(([icon, name, desc]) => (
              <div key={name} className="filiale-item">
                <span className="filiale-icon">{icon}</span>
                <div><div className="filiale-name">{name}</div><div className="filiale-desc">{desc}</div></div>
              </div>
            ))}
          </div>
          <div className="left-footer">Abomey-Calavi, Bénin · RCCM RB/ABC/22 B 5806</div>
        </div>

        <div className="right-panel">
          {formMode === 'connexion' ? (
            <div id="formConnexion">
              <div className="form-header">
                <div className="form-title">Bon retour 👋</div>
                <div className="form-sub">Connectez-vous à votre espace FHC Groupe SARL</div>
              </div>
              <div className="field">
                <label className="field-label">Adresse e-mail</label>
                <div className="input-wrap">
                  <span className="input-icon">📧</span>
                  <input type="email" className="input-field" value={loginEmail}
                    onChange={e => setLoginEmail(e.target.value)}
                    placeholder="votre@fhcgroupe.com" />
                </div>
              </div>
              <div className="field">
                <label className="field-label">Mot de passe</label>
                <div className="input-wrap">
                  <span className="input-icon">🔒</span>
                  <input type={showPwd.login ? 'text' : 'password'} className="input-field"
                    value={loginPassword} onChange={e => setLoginPassword(e.target.value)}
                    placeholder="••••••••" onKeyDown={e => e.key === 'Enter' && loginUser()} />
                  <button className="eye-btn" type="button"
                    onClick={() => setShowPwd(p => ({ ...p, login: !p.login }))}>
                    {showPwd.login ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
              {loginError && <div className="error-box">{loginError}</div>}
              <button className="btn-primary" style={{ width: '100%', marginBottom: 16 }}
                type="button" onClick={loginUser} disabled={loginLoading}>
                {loginLoading ? '⏳ Connexion...' : 'Se connecter →'}
              </button>
              {/* <div className="switch-row">
                Pas encore de compte ?
                <button className="switch-link" type="button" onClick={() => setFormMode('inscription')}>S'inscrire</button>
              </div> */}
            </div>
          ) : (
            <div id="formInscription">
              <div className="form-header">
                <div className="form-title">Créer un compte</div>
                <div className="form-sub">Rejoignez la plateforme de gestion digitale FHC</div>
              </div>
              <div className="steps">
                {[1, 2, 3].map((step) => (
                  <React.Fragment key={step}>
                    <div className="step-item">
                      <div className={`step-circle ${currentStep === step ? 'active' : currentStep > step ? 'done' : ''}`}>
                        {currentStep > step ? '✓' : step}
                      </div>
                      <div className={`step-label ${currentStep === step ? 'active' : ''}`}>
                        {step === 1 ? 'Identité' : step === 2 ? 'Poste' : 'Sécurité'}
                      </div>
                    </div>
                    {step < 3 && <div className={`step-line ${currentStep > step ? 'done' : ''}`} />}
                  </React.Fragment>
                ))}
              </div>

              {currentStep === 1 && (
                <>
                  <div className="two-col">
                    <div className="field">
                      <label className="field-label">Prénom *</label>
                      <div className="input-wrap"><span className="input-icon">✏️</span>
                        <input type="text" className="input-field" value={regValues.prenom}
                          onChange={e => setRegValues(p => ({ ...p, prenom: e.target.value }))}
                          placeholder="Ex : Karmelle" /></div>
                      {regErrors.prenom && <div className="field-error">{regErrors.prenom}</div>}
                    </div>
                    <div className="field">
                      <label className="field-label">Nom *</label>
                      <div className="input-wrap"><span className="input-icon">✏️</span>
                        <input type="text" className="input-field" value={regValues.nom}
                          onChange={e => setRegValues(p => ({ ...p, nom: e.target.value }))}
                          placeholder="Ex : HAZOUMÈ" /></div>
                      {regErrors.nom && <div className="field-error">{regErrors.nom}</div>}
                    </div>
                  </div>
                  <div className="field">
                    <label className="field-label">E-mail professionnel *</label>
                    <div className="input-wrap"><span className="input-icon">📧</span>
                      <input type="email" className="input-field" value={regValues.email}
                        onChange={e => setRegValues(p => ({ ...p, email: e.target.value }))}
                        placeholder="prenom@fhcgroupe.com" /></div>
                    {regErrors.email && <div className="field-error">{regErrors.email}</div>}
                  </div>
                  <div className="field">
                    <label className="field-label">Téléphone *</label>
                    <div className="input-wrap"><span className="input-icon">📱</span>
                      <input type="tel" className="input-field" value={regValues.tel}
                        onChange={e => setRegValues(p => ({ ...p, tel: e.target.value }))}
                        placeholder="+229 XX XX XX XX" /></div>
                    {regErrors.tel && <div className="field-error">{regErrors.tel}</div>}
                  </div>
                  <div className="btn-row">
                    <button className="btn-primary" type="button" onClick={() => nextStep(1)}>Continuer →</button>
                  </div>
                </>
              )}

              {currentStep === 2 && (
                <>
                  <div className="field">
                    <label className="field-label">Rôle *</label>
                    <div className="role-cards">
                      {['dg', 'chef', 'employe'].map(role => (
                        <button key={role} type="button" className={selectedRoleClass(role)}
                          onClick={() => { setCurrentRole(role); setRegErrors(p => ({ ...p, role: '' })); }}>
                          <div className="role-card-icon">{role === 'dg' ? '👑' : role === 'chef' ? '🏛️' : '👤'}</div>
                          <div className="role-card-label">{role === 'dg' ? 'Directeur Général' : role === 'chef' ? 'Chef Département' : 'Employé'}</div>
                          <div className="role-card-desc">{role === 'dg' ? 'Accès total' : role === 'chef' ? 'Gestion de son dept.' : 'Tâches & pointage'}</div>
                        </button>
                      ))}
                    </div>
                    {regErrors.role && <div className="field-error">{regErrors.role}</div>}
                  </div>
                  <div className="field">
                    <label className="field-label">Filiale / Entité *</label>
                    <div className="input-wrap"><span className="input-icon">🏢</span>
                      <select className="input-field" value={regValues.filiale_id}
                        onChange={e => setRegValues(p => ({ ...p, filiale_id: e.target.value }))}>
                        <option value="">— Sélectionner la filiale —</option>
                        {filiales.map(f => <option key={f.id} value={f.id}>{f.nom}</option>)}
                      </select></div>
                    {regErrors.filiale_id && <div className="field-error">{regErrors.filiale_id}</div>}
                  </div>
                  <div className="field">
                    <label className="field-label">Département *</label>
                    <div className="input-wrap"><span className="input-icon">🗂️</span>
                      <select className="input-field" value={regValues.departement_id}
                        onChange={e => setRegValues(p => ({ ...p, departement_id: e.target.value }))}>
                        <option value="">— Sélectionner le département —</option>
                        {departements.map(d => <option key={d.id} value={d.id}>{d.nom}</option>)}
                      </select></div>
                    {regErrors.departement_id && <div className="field-error">{regErrors.departement_id}</div>}
                  </div>
                  <div className="field">
                    <label className="field-label">Intitulé du poste *</label>
                    <div className="input-wrap"><span className="input-icon">💼</span>
                      <input type="text" className="input-field" value={regValues.poste}
                        onChange={e => setRegValues(p => ({ ...p, poste: e.target.value }))}
                        placeholder="Ex : Chargé de projets, Comptable..." /></div>
                    {regErrors.poste && <div className="field-error">{regErrors.poste}</div>}
                  </div>
                  <div className="btn-row">
                    <button className="btn-secondary" type="button" onClick={() => setCurrentStep(1)}>← Retour</button>
                    <button className="btn-primary" type="button" onClick={() => nextStep(2)}>Continuer →</button>
                  </div>
                </>
              )}

              {currentStep === 3 && (
                <>
                  <div className="field">
                    <label className="field-label">Mot de passe *</label>
                    <div className="input-wrap"><span className="input-icon">🔒</span>
                      <input type={showPwd.reg ? 'text' : 'password'} className="input-field"
                        value={regValues.pwd} onChange={e => setRegValues(p => ({ ...p, pwd: e.target.value }))}
                        placeholder="Minimum 6 caractères" />
                      <button className="eye-btn" type="button"
                        onClick={() => setShowPwd(p => ({ ...p, reg: !p.reg }))}>
                        {showPwd.reg ? '🙈' : '👁️'}
                      </button></div>
                    {regErrors.pwd && <div className="field-error">{regErrors.pwd}</div>}
                  </div>
                  <div className="field">
                    <label className="field-label">Confirmer le mot de passe *</label>
                    <div className="input-wrap"><span className="input-icon">🔒</span>
                      <input type={showPwd.confirm ? 'text' : 'password'} className="input-field"
                        value={regValues.confirm} onChange={e => setRegValues(p => ({ ...p, confirm: e.target.value }))}
                        placeholder="Répétez le mot de passe" />
                      <button className="eye-btn" type="button"
                        onClick={() => setShowPwd(p => ({ ...p, confirm: !p.confirm }))}>
                        {showPwd.confirm ? '🙈' : '👁️'}
                      </button></div>
                    {regErrors.confirm && <div className="field-error">{regErrors.confirm}</div>}
                  </div>
                  {regErrors.general && <div className="error-box">{regErrors.general}</div>}
                  <div className={`success-msg${successMessage ? ' show' : ''}`} style={{ display: successMessage ? 'block' : 'none' }}>
                    ✅ Compte créé avec succès ! Redirection...
                  </div>
                  <div className="btn-row">
                    <button className="btn-secondary" type="button" onClick={() => setCurrentStep(2)}>← Retour</button>
                    <button className="btn-primary" type="button" onClick={handleRegister}>✅ Créer mon compte</button>
                  </div>
                </>
              )}

              <div className="switch-row">
                Déjà un compte ?
                <button className="switch-link" type="button" onClick={() => setFormMode('connexion')}>Se connecter</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AuthLayout>
  );
}

export default AuthPage;
